import React, { useCallback, useState } from 'react';
import { 
    Box, Typography, Button, Card, CardContent, useMediaQuery, useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    useSalonsCategoriesListQuery,
    useServicesSalonListQuery,
    useTeamsTeamMembersListQuery,
    useSchedulesSchedulesListQuery
} from '../../../services/GLOWClient';
import theme from '../../../theme';
import FullScreenDialog from '../../../SystemDesign/FullScreenDialog';
import { 
    SecondaryOutlinedButton,
    TitleScreen,
} from '../../../SystemDesign/styles';
import AddCategory from './category/addCategory';
import Category from './category/category';
import AddService from './service/addService';
import Service from './service/service';
import ServiceCategorySectionSequential from './serviceCategorySectionSequential';
import MemberPlanningSectionSequential from './memberPlanningSectionSequential';
import AddMember from '../employee/teamMember/addmember';
import TeamMembers from '../employee/teamMember/team_members';
import Team from '../employee/team/team';
import AddSchedule from '../employee/schedule/addSchedule';
import Schedule from '../employee/schedule/schedule';
import CategoryIcon from '@mui/icons-material/Category';
import SpaIcon from '@mui/icons-material/Spa';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import BusinessIcon from '@mui/icons-material/Business';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import SalonClosures from './salonClosures';
import SalonPromotions from './salonPromotions';

const SalonManagement = ({ isUpdateMode, salonToUpdate, isCreating, isUpdating, onSuccess, onSalonCreated, onClose }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    
    // Section management state
    const [currentSection, setCurrentSection] = useState(0);
    const sections = [t('pro_dashboard.salon.sections.services'), t('pro_dashboard.salon.sections.employees_planning'), t('pro_dashboard.salon.sections.closures'), t('pro_dashboard.salon.sections.promotions')];
    
    // Mobile responsiveness
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    
    // State for dialogs
    const [isAddCategoryDialogOpen, setIsAddCategoryDialogOpen] = useState(false);
    const [isViewCategoriesDialogOpen, setIsViewCategoriesDialogOpen] = useState(false);
    const [isAddServiceDialogOpen, setIsAddServiceDialogOpen] = useState(false);
    const [isViewServicesDialogOpen, setIsViewServicesDialogOpen] = useState(false);
    const [isViewCategoriesServicesDialogOpen, setIsViewCategoriesServicesDialogOpen] = useState(false);
    const [isServiceCategorySectionOpen, setIsServiceCategorySectionOpen] = useState(false);
    const [selectedCategoryForService, setSelectedCategoryForService] = useState(null);
    const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
    const [isViewTeamsDialogOpen, setIsViewTeamsDialogOpen] = useState(false);
    const [isViewTeamMembersDialogOpen, setIsViewTeamMembersDialogOpen] = useState(false);
    const [isAddScheduleDialogOpen, setIsAddScheduleDialogOpen] = useState(false);
    const [isViewSchedulesDialogOpen, setIsViewSchedulesDialogOpen] = useState(false);
    const [isMemberPlanningSectionOpen, setIsMemberPlanningSectionOpen] = useState(false);
    
    // Removed debug logging to improve performance
    
    // Local state for entities
    const [localCategories, setLocalCategories] = useState([]);
    const [localServices, setLocalServices] = useState([]);
    const [localTeamMembers, setLocalTeamMembers] = useState([]);
    const [localSchedules, setLocalSchedules] = useState([]);
    const [createdSalonId, setCreatedSalonId] = useState(null);

    // Get salon ID
    const salonId = salonToUpdate?.id || salonToUpdate?.salon_id || salonToUpdate?.pk;
    const shouldFetchData = isUpdateMode && salonToUpdate && salonId;
    const effectiveSalonId = salonId || createdSalonId;
    
    // Salon ID resolution complete

    // Data fetching
    const { data: categoriesData, refetch: refetchCategories } = useSalonsCategoriesListQuery(
        shouldFetchData ? { salonId: salonId, language: i18n.language } : {},
        { skip: !shouldFetchData }
    );
    
    const categories = shouldFetchData ? 
        (Array.isArray(categoriesData) ? categoriesData : (categoriesData?.results || [])) : 
        localCategories;
    
    // Categories data processing

    const { data: servicesData, refetch: refetchServices } = useServicesSalonListQuery(
        shouldFetchData ? { salonId: salonId, language: i18n.language } : {},
        { skip: !shouldFetchData }
    );
    
    // Services data processing
    
    const services = shouldFetchData ? 
        (Array.isArray(servicesData) ? servicesData : (servicesData?.results || [])) : 
        localServices;

    const { data: teamMembersData, refetch: refetchTeamMembers } = useTeamsTeamMembersListQuery(
        shouldFetchData ? { salon: salonId, language: i18n.language } : {},
        { skip: !shouldFetchData }
    );
    
    // Team members data processing
    
    const teamMembers = shouldFetchData ? 
        (Array.isArray(teamMembersData) ? teamMembersData : (teamMembersData?.results || [])) : 
        localTeamMembers;

    const { data: schedulesData, refetch: refetchSchedules } = useSchedulesSchedulesListQuery(
        shouldFetchData ? { salon: salonId, language: i18n.language } : {},
        { skip: !shouldFetchData }
    );
    
    // Schedules data processing
    
    const schedules = shouldFetchData ? 
        (Array.isArray(schedulesData) ? schedulesData : (schedulesData?.results || [])) : 
        localSchedules;

    const currentLanguage = (i18n.language || 'fr').split('-')[0].toLowerCase();

    const getTranslatedField = useCallback((item, field) => {
        const translationsKey = `${field}_translations`;
        const localizedKey = `localized_${field}`;
        const translations = item?.[translationsKey] || {};

        if (translations[currentLanguage]) {
            return translations[currentLanguage];
        }

        if (item?.[localizedKey]) {
            return item[localizedKey];
        }

        for (const fallbackLanguage of ['fr', 'en', 'ar']) {
            if (translations[fallbackLanguage]) {
                return translations[fallbackLanguage];
            }
        }

        return item?.[field] || '';
    }, [currentLanguage]);

    // Handle success operations
    const handleSuccessWithRefetch = (message, data) => {
        const messageStr = typeof message === 'string' ? message : '';
        if (shouldFetchData) {
            if (messageStr.includes(t('pro_dashboard.salon.category_key'))) refetchCategories();
            else if (messageStr.includes('prestation')) refetchServices();
            else if (messageStr.includes('horaire')) refetchSchedules();
            else if (messageStr.includes('membre')) refetchTeamMembers();
            if (onSuccess) onSuccess(messageStr);
        } else {
            if (messageStr.includes(t('pro_dashboard.salon.category_key')) && data) {
                setLocalCategories(prev => [...prev, { ...data, id: `temp-${Date.now()}` }]);
            } else if (messageStr.includes('prestation') && data) {
                setLocalServices(prev => [...prev, { ...data, id: `temp-${Date.now()}` }]);
            } else if (messageStr.includes('membre') && data) {
                setLocalTeamMembers(prev => [...prev, { ...data, id: `temp-${Date.now()}` }]);
            } else if (messageStr.includes('horaire') && data) {
                setLocalSchedules(prev => [...prev, { ...data, id: `temp-${Date.now()}` }]);
            }
            
            const currentSalonId = salonId || data?.salon_id;
            if (currentSalonId && !createdSalonId) {
                setCreatedSalonId(currentSalonId);
                processPendingEntities(currentSalonId);
            }
            
            if (onSuccess) onSuccess(messageStr);
        }
    };

    // Process pending entities
    const processPendingEntities = async (salonId) => {
        if (localCategories.length > 0) {
            for (const category of localCategories) {
                try {
                    // await createCategoryApi({ ...category, salon: salonId });
                } catch (error) {
                    console.error('Error creating category:', error);
                }
            }
        }
        
        if (localServices.length > 0) {
            for (const service of localServices) {
                try {
                    // await createServiceApi({ ...service, salon: salonId });
                } catch (error) {
                    console.error('Error creating prestations:', error);
                }
            }
        }
        
        if (localTeamMembers.length > 0) {
            for (const member of localTeamMembers) {
                try {
                    // await createTeamMemberApi({ ...member, team: salonId });
                } catch (error) {
                    console.error('Error creating team member:', error);
                }
            }
        }
        
        if (localSchedules.length > 0) {
            for (const schedule of localSchedules) {
                try {
                    // await createScheduleApi({ ...schedule, salon: salonId });
                } catch (error) {
                    console.error('Error creating schedule:', error);
                }
            }
        }
        
        setLocalCategories([]);
        setLocalServices([]);
        setLocalTeamMembers([]);
        setLocalSchedules([]);
    };

    // Navigation functions
    const handleNext = () => {
        if (currentSection < sections.length - 1) {
            setCurrentSection(currentSection + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSection > 0) {
            setCurrentSection(currentSection - 1);
        }
    };

    const handleQuit = () => {
        // Close all open dialogs
        setIsAddCategoryDialogOpen(false);
        setIsViewCategoriesDialogOpen(false);
        setIsAddServiceDialogOpen(false);
        setIsViewServicesDialogOpen(false);
        setIsViewCategoriesServicesDialogOpen(false);
        setIsServiceCategorySectionOpen(false);
        setIsAddMemberDialogOpen(false);
        setIsViewTeamsDialogOpen(false);
        setIsViewTeamMembersDialogOpen(false);
        setIsAddScheduleDialogOpen(false);
        setIsViewSchedulesDialogOpen(false);
        setIsMemberPlanningSectionOpen(false);
        setSelectedCategoryForService(null);
        
        // Reset to first section
        // setCurrentSection(0);
        
        // Call onSuccess first if available
        if (onSuccess) {
            onSuccess(t('pro_dashboard.salon.management_complete'));
        }
        
        // Close the parent dialog - this is the main fix
        if (onClose) {
            onClose();
        }
        
        // Redirect to salon list
        navigate('/pro/salon');
    };

    // Section components
    const renderSalonCreationSection = () => (
        <Card sx={{ 
            mb: { xs: 1, md: 4 }, 
            borderRadius: { xs: 0, md: theme.borderRadius.xl },
            boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
            border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
            backgroundColor: { xs: 'transparent', md: 'white' }
        }}>
            <CardContent sx={{ p: { xs: 1, md: 4 } }}>
                <Typography 
                    variant="h5" 
                    sx={{ 
                        ...TitleScreen(theme.colors),
                        mb: 3, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: theme.colors.primary[700],
                        fontWeight: 'bold'
                    }}
                >
                    <BusinessIcon sx={{ 
                        mr: 1.5, 
                        color: theme.colors.primary[500],
                        fontSize: '2rem'
                    }} />
                    {t('pro_dashboard.salon.salon_creation')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.colors.primary[600], mb: 3 }}>
                    {t('pro_dashboard.salon.creation_desc')}
                </Typography>
                {isUpdateMode && salonToUpdate && (
                    <Box sx={{ 
                        p: 3, 
                        bgcolor: theme.colors.secondary[50], 
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.secondary[200]}`
                    }}>
                        <Typography variant="h6" sx={{ color: theme.colors.primary[700], mb: 1 }}>
                            {t('pro_dashboard.salon.salon_name_label', { name: salonToUpdate.name || t('pro_dashboard.salon.not_defined') })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.colors.primary[500] }}>
                            {t('pro_dashboard.salon.update_mode')}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    const renderCategoriesServicesSection = () => (
        <ServiceCategorySectionSequential
            onSuccess={(message) => handleSuccessWithRefetch(message)}
            onQuit={handleQuit}
            selectedSalonId={salonId}
        />
    );

    const renderEmployeesSection = () => (
        <MemberPlanningSectionSequential
            onSuccess={(message) => handleSuccessWithRefetch(message)}
            onQuit={handleQuit}
            selectedSalonId={salonId}
        />
    );

    const renderClosuresSection = () => {
        if (!effectiveSalonId) {
            return (
                <Card sx={{
                    mb: { xs: 1, md: 4 },
                    borderRadius: { xs: 0, md: theme.borderRadius.xl },
                    boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
                    border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
                    backgroundColor: { xs: 'transparent', md: 'white' }
                }}>
                    <CardContent sx={{ p: { xs: 1, md: 4 } }}>
                        <Typography variant="h6" sx={{ color: theme.colors.primary[700], fontWeight: 'bold', mb: 1 }}>
                            {t('pro_dashboard.salon.sections.closures')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.colors.primary[600] }}>
                            {t('pro_dashboard.salon.create_salon_first_closures')}
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card sx={{
                mb: { xs: 1, md: 4 },
                borderRadius: { xs: 0, md: theme.borderRadius.xl },
                boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
                border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
                backgroundColor: { xs: 'transparent', md: 'white' }
            }}>
                <CardContent sx={{ p: { xs: 1, md: 4 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <EventBusyIcon sx={{ color: theme.colors.primary[500] }} />
                        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: theme.colors.primary[700], fontWeight: 'bold' }}>
                            {t('pro_dashboard.salon.sections.closures')}
                        </Typography>
                    </Box>
                    <SalonClosures initialSalonId={effectiveSalonId} embedded />
                </CardContent>
            </Card>
        );
    };

    const renderPromotionsSection = () => {
        return (
            <SalonPromotions
                salonId={effectiveSalonId}
                categories={categories}
                services={services}
            />
        );
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: { xs: 0, md: 'auto' }, py: { xs: 0, md: 4 }, px: { xs: 0, md: 0 } }}>
            {/* Section Header with Stepper */}
            <Card sx={{ 
                mb: { xs: 1, md: 4 }, 
                borderRadius: { xs: 0, md: theme.borderRadius.xl },
                boxShadow: { xs: 'none', md: `0 4px 20px ${theme.colors.primary[50]}` },
                border: { xs: 'none', md: `1px solid ${theme.colors.primary[100]}` },
                backgroundColor: { xs: 'transparent', md: 'white' }
            }}>
                <CardContent sx={{ p: { xs: 1, md: 4 } }}>
                    <Typography 
                        variant={isMobile ? "h5" : "h4"} 
                        sx={{ 
                            ...TitleScreen(theme.colors),
                            mb: isMobile ? 3 : 4, 
                            textAlign: 'center',
                            color: theme.colors.primary[700],
                            fontWeight: 'bold',
                            fontSize: isMobile ? '1.5rem' : '2rem'
                        }}
                    >
                        {t('pro_dashboard.salon.management_title')}
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: isMobile ? 0.5 : 4, 
                        mb: 4,
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        width: '100%'
                    }}>
                        {sections.map((label, index) => (
                            <Button
                                key={label}
                                onClick={() => setCurrentSection(index)}
                                sx={{
                                    px: isMobile ? 1 : 3,
                                    py: isMobile ? 1 : 1.5,
                                    borderRadius: theme.borderRadius.lg,
                                    backgroundColor: index === currentSection ? theme.colors.primary[500] : 'transparent',
                                    color: index === currentSection ? theme.colors.primary[50] : theme.colors.primary[600],
                                    border: `2px solid ${index === currentSection ? theme.colors.primary[500] : theme.colors.primary[200]}`,
                                    fontWeight: index === currentSection ? 'bold' : 'medium',
                                    fontSize: isMobile ? '0.65rem' : '0.9rem',
                                    minHeight: isMobile ? 36 : 'auto',
                                    flex: isMobile ? '0 0 calc(50% - 4px)' : 'none',
                                    maxWidth: isMobile ? 'calc(50% - 4px)' : 'none',
                                    minWidth: isMobile ? 'calc(50% - 4px)' : 'auto',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: index === currentSection ? theme.colors.primary[600] : theme.colors.primary[50],
                                        borderColor: theme.colors.primary[500],
                                        transform: isMobile ? 'none' : 'translateY(-2px)',
                                        boxShadow: `0 4px 12px ${theme.colors.primary[100]}`,
                                    }
                                }}
                            >
                                {isMobile ? label.replace(t('pro_dashboard.salon.salon_creation'), t('pro_dashboard.salon.creation')).replace(t('pro_dashboard.salon.prestations_key'), t('pro_dashboard.salon.services_short')).replace(t('pro_dashboard.salon.employees_key'), t('pro_dashboard.salon.employees')).replace(t('pro_dashboard.salon.closures_key'), t('pro_dashboard.salon.closures_short')).replace(t('pro_dashboard.salon.promotions_key'), t('pro_dashboard.salon.promotions_short')) : label}
                            </Button>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            {/* Current Section Content */}
            {currentSection === 0 && renderCategoriesServicesSection()}
            {currentSection === 1 && renderEmployeesSection()}
            {currentSection === 2 && renderClosuresSection()}
            {currentSection === 3 && renderPromotionsSection()}

            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mt: isMobile ? 1 : 2,
                px: isMobile ? 1 : 2
            }}>
                <Button
                    sx={{
                        ...SecondaryOutlinedButton,
                        borderColor: theme.colors.error[300],
                        color: theme.colors.error[600],
                        '&:hover': {
                            borderColor: theme.colors.error[400],
                            backgroundColor: theme.colors.error[50],
                            transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                        px: isMobile ? 2 : 4,
                        py: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        minWidth: isMobile ? 'auto' : '120px',
                        width: isMobile ? '100%' : 'auto',
                        maxWidth: isMobile ? '200px' : 'none',
                        borderRadius: theme.borderRadius.lg
                    }}
                    startIcon={<ExitToAppIcon sx={{
                        fontSize: isMobile ? '1rem' : '1.2rem'
                    }} />}
                    onClick={handleQuit}
                >
                    {t('pro_dashboard.salon.quit')}
                </Button>
            </Box>


            {/* Dialogs */}
            <AddCategory 
                open={isAddCategoryDialogOpen} 
                onClose={() => setIsAddCategoryDialogOpen(false)} 
                onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.category_added'), data)}
                selectedSalonId={salonId}
                isCreationMode={!isUpdateMode}
            />


            <FullScreenDialog
                open={isViewCategoriesDialogOpen}
                onExternalClose={() => {
                    setIsViewCategoriesDialogOpen(false);
                }}
                title={t('pro_dashboard.salon.manage_categories')}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: theme.colors.primary[50],
                        borderRadius: theme.borderRadius.xl
                    }
                }}
            >
                <Box sx={{ p: 4, bgcolor: theme.colors.primary[50] }}>
                    {isUpdateMode ? (
                        <Category 
                            salonId={salonId} 
                            onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.category_updated'), data)} 
                        />
                    ) : (
                        localCategories.length > 0 ? (
                            localCategories.map(category => (
                                <Card key={category.id} sx={{ 
                                    mb: 3, 
                                    p: 3, 
                                    border: `1px solid ${theme.colors.primary[100]}`,
                                    borderRadius: theme.borderRadius.lg,
                                    boxShadow: `0 2px 12px ${theme.colors.primary[50]}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 20px ${theme.colors.primary[100]}`,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.colors.primary[700],
                                        fontWeight: 'bold',
                                        mb: 1
                                    }}>
                                        {getTranslatedField(category, 'name')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[400],
                                        lineHeight: 1.6
                                    }}>
                                        {getTranslatedField(category, 'description') || t('pro_dashboard.salon.no_description')}
                                    </Typography>
                                </Card>
                            ))
                        ) : (
                            <Typography sx={{ 
                                color: theme.colors.primary[300],
                                textAlign: 'center',
                                fontStyle: 'italic',
                                py: 4,
                                bgcolor: theme.colors.primary[50],
                                borderRadius: theme.borderRadius.md
                            }}>
                                {t('pro_dashboard.salon.no_category_added')}
                            </Typography>
                        )
                    )}
                </Box>
            </FullScreenDialog>

            <AddService 
                open={isAddServiceDialogOpen} 
                onClose={() => {
                    setIsAddServiceDialogOpen(false);
                    setSelectedCategoryForService(null);
                }} 
                onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.service_added'), data)}
                salonId={salonId}
                categories={isUpdateMode ? categories : localCategories}
                selectedCategory={selectedCategoryForService}
                isCreationMode={!isUpdateMode}
            />

            <FullScreenDialog
                open={isViewServicesDialogOpen}
                onExternalClose={() => setIsViewServicesDialogOpen(false)}
                title={t('pro_dashboard.salon.manage_services')}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: theme.colors.primary[50],
                        borderRadius: theme.borderRadius.xl
                    }
                }}
            >
                <Box sx={{ p: 4, bgcolor: theme.colors.primary[50] }}>
                    {isUpdateMode ? (
                        <Service 
                            salonId={salonId} 
                            onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.service_updated'), data)} 
                        />
                    ) : (
                        localServices.length > 0 ? (
                            localServices.map(service => (
                                <Card key={service.id} sx={{ 
                                    mb: 3, 
                                    p: 3, 
                                    border: `1px solid ${theme.colors.primary[100]}`,
                                    borderRadius: theme.borderRadius.lg,
                                    boxShadow: `0 2px 12px ${theme.colors.primary[50]}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 20px ${theme.colors.primary[100]}`,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.colors.primary[700],
                                        fontWeight: 'bold',
                                        mb: 1
                                    }}>
                                        {getTranslatedField(service, 'name')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[400],
                                        mb: 1,
                                        lineHeight: 1.6
                                    }}>
                                        {getTranslatedField(service, 'description') || t('pro_dashboard.salon.no_description')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[500],
                                        fontWeight: 'medium'
                                    }}>
                                        {t('pro_dashboard.salon.price', { price: service.price })}
                                    </Typography>
                                </Card>
                            ))
                        ) : (
                            <Typography sx={{ 
                                color: theme.colors.primary[300],
                                textAlign: 'center',
                                fontStyle: 'italic',
                                py: 4,
                                bgcolor: theme.colors.primary[50],
                                borderRadius: theme.borderRadius.md
                            }}>
                                {t('pro_dashboard.salon.no_services')}
                            </Typography>
                        )
                    )}
                </Box>
            </FullScreenDialog>

            <AddMember
                open={isAddMemberDialogOpen}
                onClose={() => setIsAddMemberDialogOpen(false)}
                onSuccess={() => handleSuccessWithRefetch(t('pro_dashboard.salon.member_added'))}
                selectedSalonId={salonId}
            />

            <FullScreenDialog
                open={isViewTeamsDialogOpen}
                onExternalClose={() => setIsViewTeamsDialogOpen(false)}
                title={t('pro_dashboard.salon.manage_teams')}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: theme.colors.primary[50],
                        borderRadius: theme.borderRadius.xl
                    }
                }}
            >
                <Box sx={{ p: 4, bgcolor: theme.colors.primary[50] }}>
                    {isUpdateMode ? (
                        <Team 
                            salonId={salonId} 
                            onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.team_updated'), data)} 
                        />
                    ) : (
                        localTeamMembers.length > 0 ? (
                            localTeamMembers.map(team => (
                                <Card key={team.id} sx={{ 
                                    mb: 3, 
                                    p: 3, 
                                    border: `1px solid ${theme.colors.primary[100]}`,
                                    borderRadius: theme.borderRadius.lg,
                                    boxShadow: `0 2px 12px ${theme.colors.primary[50]}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 20px ${theme.colors.primary[100]}`,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.colors.primary[700],
                                        fontWeight: 'bold',
                                        mb: 1
                                    }}>
                                        {team.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[400],
                                        lineHeight: 1.6
                                    }}>
                                        {team.description || t('pro_dashboard.salon.no_description')}
                                    </Typography>
                                </Card>
                            ))
                        ) : (
                            <Typography sx={{ 
                                color: theme.colors.primary[300],
                                textAlign: 'center',
                                fontStyle: 'italic',
                                py: 4,
                                bgcolor: theme.colors.primary[50],
                                borderRadius: theme.borderRadius.md
                            }}>
                                {t('pro_dashboard.salon.no_teams')}
                            </Typography>
                        )
                    )}
                </Box>
            </FullScreenDialog>

            <FullScreenDialog
                open={isViewTeamMembersDialogOpen}
                onExternalClose={() => setIsViewTeamMembersDialogOpen(false)}
                title={t('pro_dashboard.salon.manage_team_members')}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: theme.colors.primary[50],
                        borderRadius: theme.borderRadius.xl
                    }
                }}
            >
                <Box sx={{ p: 4, bgcolor: theme.colors.primary[50] }}>
                    {isUpdateMode ? (
                        <TeamMembers 
                            salonId={salonId} 
                            onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.member_updated'), data)} 
                        />
                    ) : (
                        localTeamMembers.length > 0 ? (
                            localTeamMembers.map(member => (
                                <Card key={member.id} sx={{ 
                                    mb: 3, 
                                    p: 3, 
                                    border: `1px solid ${theme.colors.primary[100]}`,
                                    borderRadius: theme.borderRadius.lg,
                                    boxShadow: `0 2px 12px ${theme.colors.primary[50]}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 20px ${theme.colors.primary[100]}`,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.colors.primary[700],
                                        fontWeight: 'bold',
                                        mb: 1
                                    }}>
                                        {member.user_details?.first_name} {member.user_details?.last_name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[400],
                                        lineHeight: 1.6
                                    }}>
                                        {member.description || t('pro_dashboard.salon.no_description')}
                                    </Typography>
                                </Card>
                            ))
                        ) : (
                            <Typography sx={{ 
                                color: theme.colors.primary[300],
                                textAlign: 'center',
                                fontStyle: 'italic',
                                py: 4,
                                bgcolor: theme.colors.primary[50],
                                borderRadius: theme.borderRadius.md
                            }}>
                                {t('pro_dashboard.salon.no_team_members')}
                            </Typography>
                        )
                    )}
                </Box>
            </FullScreenDialog>

            <AddSchedule
                open={isAddScheduleDialogOpen}
                onClose={() => setIsAddScheduleDialogOpen(false)}
                onSuccess={() => handleSuccessWithRefetch(t('pro_dashboard.salon.schedule_added'))}
                selectedSalonId={salonId}
            />

            <FullScreenDialog
                open={isViewSchedulesDialogOpen}
                onExternalClose={() => setIsViewSchedulesDialogOpen(false)}
                title={t('pro_dashboard.salon.manage_schedules')}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: theme.colors.primary[50],
                        borderRadius: theme.borderRadius.xl
                    }
                }}
            >
                <Box sx={{ p: 4, bgcolor: theme.colors.primary[50] }}>
                    {isUpdateMode ? (
                        <Schedule 
                            salonId={salonId} 
                            onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.schedule_updated'), data)} 
                        />
                    ) : (
                        localSchedules.length > 0 ? (
                            localSchedules.map(schedule => (
                                <Card key={schedule.id} sx={{ 
                                    mb: 3, 
                                    p: 3, 
                                    border: `1px solid ${theme.colors.primary[100]}`,
                                    borderRadius: theme.borderRadius.lg,
                                    boxShadow: `0 2px 12px ${theme.colors.primary[50]}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 20px ${theme.colors.primary[100]}`,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.colors.primary[700],
                                        fontWeight: 'bold',
                                        mb: 1
                                    }}>
                                        {schedule.team_member?.user_details?.first_name || t('pro_dashboard.salon.member_not_specified')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[400],
                                        mb: 0.5
                                    }}>
                                        {t('pro_dashboard.salon.day', { day: schedule.day_of_week })}
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.colors.primary[400],
                                        lineHeight: 1.6
                                    }}>
                                        {t('pro_dashboard.salon.time', { start: schedule.start_time, end: schedule.end_time })}
                                    </Typography>
                                </Card>
                            ))
                        ) : (
                            <Typography sx={{ 
                                color: theme.colors.primary[300],
                                textAlign: 'center',
                                fontStyle: 'italic',
                                py: 4,
                                bgcolor: theme.colors.primary[50],
                                borderRadius: theme.borderRadius.md
                            }}>
                                {t('pro_dashboard.salon.no_schedules')}
                            </Typography>
                        )
                    )}
                </Box>
            </FullScreenDialog>

            {/* Combined Categories and Services Dialog */}
            <FullScreenDialog
                open={isViewCategoriesServicesDialogOpen}
                onExternalClose={() => setIsViewCategoriesServicesDialogOpen(false)}
                title={t('pro_dashboard.salon.categories_services')}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: theme.colors.primary[50],
                        borderRadius: theme.borderRadius.xl
                    }
                }}
            >
                <Box sx={{ bgcolor: theme.colors.primary[50], minHeight: '100%' }}>
                    <Service 
                        salonId={salonId} 
                        onSuccess={(data) => handleSuccessWithRefetch(t('pro_dashboard.salon.service_updated'), data)} 
                    />
                </Box>
            </FullScreenDialog>

           
        </Box>
    );
};

export default SalonManagement;